import React from 'react';
import _ from 'lodash';

/**
 * @typedef {Object} QuickFiltersContext
 *
 * @property {boolean} isEnabled
 * Returns whether the quick filters feature is enabled.
 *
 * @property {(key: string) => string | undefined} get
 * Gets the filter string for the given key.
 *
 * @property {() => boolean} any
 * Checks if at least one filter is set.
 *
 * @property {(key: string) => boolean} has
 * Checks if the given key has a filter assigned.
 *
 * @property {(set: string, value: string) => void} set
 * Sets the filter string for the given key.
 *
 * @property {(key: string) => void} unset
 * Removes the filter string for the given key.
 *
 * @property {() => void} clear
 * Clears all filters.
 *
 * @property {() => Array<string>} values
 * Returns all set filter strings.
 */

/** @type {React.Context<QuickFiltersContext>} */
const QuickFiltersContext = React.createContext();

export const WithQuickFilters = ({children}) => {
    const isQuickFiltersEnabled = true;

    const [filters, setFilters] = React.useState({});

    const any = React.useCallback(() => _.some(filters), [filters]);
    const get = React.useCallback((key) => filters[key], [filters]);
    const has = React.useCallback((key) => _.has(filters, key), [filters]);
    const values = React.useCallback(() => _.values(filters), [filters]);

    const set = React.useCallback((key, value) => {
        setFilters((state) => ({...state, [key]: value}));
    }, []);

    const unset = React.useCallback((key) => {
        setFilters((state) => _.omit(state, key));
    }, []);

    return (
        <QuickFiltersContext.Provider value={{any, get, has, set, values, unset, isEnabled: isQuickFiltersEnabled}}>
            {children}
        </QuickFiltersContext.Provider>
    );
};

/**
 * A higher-order component that provides a context for working with quick filters.
 *
 * @param {React.JSX.Element} Component
 * The component to provide the context to.
 *
 * @returns {React.JSX.Element}
 * The provided `Component`, wrapped in a `QuickFiltersContext` provider.
 */
export const withQuickFilters = (Component) => {
    const WithQuickFiltersHOC = (props) => (
        <WithQuickFilters>
            <Component {...props} />
        </WithQuickFilters>
    );
    WithQuickFiltersHOC.displayName = `WithQuickFilters(${Component.displayName || Component.name || 'Component'})`;
    return WithQuickFiltersHOC;
};

/**
 * A React hook that provides a context for working with quick filters.
 * @returns {QuickFiltersContext}
 */
export const useQuickFilters = () => {
    const context = React.useContext(QuickFiltersContext);
    if (!context) {
        throw new Error('useQuickFilters must be used within a QuickFiltersContext');
    }

    return context;
};
